import React from 'react';
import { Link } from 'react-router-dom';
import './Hero.css';

const Hero = ({ featuredBlogs }) => {
  if (!featuredBlogs || featuredBlogs.length === 0) {
    return null;
  }

  const heroBlogs = featuredBlogs.slice(0, 4);

  // Extract first <p> tag text from content_html
  const getFirstParagraph = (html) => {
    if (!html) return '';
    const match = html.match(/<p[^>]*>(.*?)<\/p>/s);
    if (!match) return '';
    // Strip all HTML tags from the matched paragraph
    return match[1].replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  };

  const extractData = (blog) => {
    const blogData = blog.SEO || blog.metadata || blog;
    const title = blogData.title || 'Featured Article';
    const image = blogData.image || blogData.image_url;
    const category = blogData.category || 'Tech';
    const date = blog.updated_at || blog.created_at || blogData.last_updated || blogData.created_at || blogData.date || blog.date || '';
    const author = blogData.author || 'AI Research Team';

    // Prefer first paragraph from content_html
    const firstParagraph = getFirstParagraph(blog.content_html);
    const fallbackExcerpt = blogData.content_preview || blogData.content || '';
    const rawExcerpt = firstParagraph || fallbackExcerpt;

    const cleanExcerpt = rawExcerpt
      .replace(/```html/g, '')
      .replace(/```/g, '')
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const displayTitle = title.includes(':') ? title.split(':')[1].trim() : title;

    return { displayTitle, cleanExcerpt, image, category, date, author, id: blog.id };
  };

  const mainArticle = extractData(heroBlogs[0]);
  const secondaryArticles = heroBlogs.slice(1).map(extractData);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return dateString;
      
      const datePart = d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      
      if (dateString.includes('T') || dateString.includes(':')) {
        const timePart = d.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        return `${datePart} at ${timePart}`;
      }
      return datePart;
    } catch (e) {
      return dateString;
    }
  };

  return (
    <section className="nyt-highlights">
      <div className="nyt-highlights-container">
        <h2 className="nyt-section-label">Highlights</h2>
        <div className="nyt-highlights-divider"></div>

        <div className="nyt-highlights-grid">
          {/* Main Featured Article */}
          <div className="nyt-main-feature">
            <Link to={`/blog/${mainArticle.id}`} className="nyt-article-link">
              {mainArticle.image && (
                <div className="nyt-main-image">
                  <img src={mainArticle.image} alt={mainArticle.displayTitle} width="800" height="450" loading="eager" fetchPriority="high" />
                </div>
              )}
              <div className="nyt-main-content">
                <h3 className="nyt-main-title">{mainArticle.displayTitle}</h3>
                <p className="nyt-main-excerpt">
                  {mainArticle.cleanExcerpt.length > 280
                    ? mainArticle.cleanExcerpt.substring(0, 280) + '...'
                    : mainArticle.cleanExcerpt}
                </p>
                <div className="nyt-main-meta">
                  <span className="nyt-meta-author">By {mainArticle.author}</span>
                  <span className="nyt-meta-date">{formatDate(mainArticle.date)}</span>
                </div>
              </div>
            </Link>
          </div>

          {/* Secondary Articles Stack */}
          <div className="nyt-secondary-stack">
            {secondaryArticles.map((article, index) => (
              <Link
                key={article.id}
                to={`/blog/${article.id}`}
                className={`nyt-secondary-item ${index < secondaryArticles.length - 1 ? 'nyt-secondary-bordered' : ''}`}
              >
                <div className="nyt-secondary-text">
                  <h4 className="nyt-secondary-title">{article.displayTitle}</h4>
                  <p className="nyt-secondary-excerpt">
                    {article.cleanExcerpt.length > 120
                      ? article.cleanExcerpt.substring(0, 120) + '...'
                      : article.cleanExcerpt}
                  </p>
                  <div className="nyt-secondary-meta">
                    <span className="nyt-secondary-author">By {article.author}</span>
                    {article.date && (
                      <span className="nyt-secondary-date">{formatDate(article.date)}</span>
                    )}
                  </div>
                </div>
                {article.image && (
                  <div className="nyt-secondary-thumb">
                    <img src={article.image} alt={article.displayTitle} width="120" height="90" loading="lazy" />
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;